import React, { useRef, useState } from "react";
import { LegalFooter } from "@dowty/legal";
import { AppShell } from "@dowty/ui";
import type { NavItem } from "@dowty/ui";
import { Alert, Badge, Button, Drawer, Grid, Modal } from "antd";
import {
    AppstoreAddOutlined,
    AreaChartOutlined,
    BarChartOutlined,
    CalendarOutlined,
    CopyOutlined,
    CrownFilled,
    IdcardOutlined,
    LogoutOutlined,
    MoonFilled,
    PlusOutlined,
    RocketOutlined,
    SunFilled,
    TrophyOutlined,
    TranslationOutlined,
    HomeOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useOutbox } from "@dowty/offline";
import { outbox } from "../offline";
import AddDrink from "./drinks/AddDrink";
import { useTranslation } from "react-i18next";
import { useDesignMode } from "../theme/DesignMode";
import { useAppFeedback } from "./useAppFeedback";
import InstallPrompt from "./InstallPrompt";
import { dowtexHubUrl, startSignIn } from "../auth/signIn";
import { PushLogoutOfflineError } from "../services/pushService";


const Sidebar: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { message } = useAppFeedback();
    const quickAddOpener = useRef<HTMLElement | null>(null);
    const [quickAddOpen, setQuickAddOpen] = useState(false);
    const { isAuthenticated, isSsoSession, logout, user } = useAuth();
    const { pendingCount } = useOutbox(outbox);
    const { mode, toggle } = useDesignMode();
    const location = useLocation();
    const navigate = useNavigate();
    const isDesktop = Grid.useBreakpoint().md;
    const { t, i18n } = useTranslation();
    const german = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase().startsWith("de");

    const items: NavItem[] = [
        { href: "/", label: isDesktop ? t("pages.drinkhelper.title") : t("sidebar.mobileHome"), icon: <AppstoreAddOutlined /> },
        { href: "/drinks/stats", label: t("sidebar.navDrinkStats"), icon: <BarChartOutlined /> },
        { href: "/drinks/history", label: t("sidebar.navDrinkHistory"), icon: <CalendarOutlined /> },
        { href: "/events/stats", label: t("sidebar.navEventStats"), icon: <AreaChartOutlined /> },
        { href: "/profile", label: t("sidebar.navProfile"), icon: <IdcardOutlined /> },
        { href: "/tournaments", label: "Tournaments", icon: <TrophyOutlined /> },
        { href: "/beerpong", label: t("sidebar.navBeerpong"), icon: <RocketOutlined /> },
        { href: "/kingCup", label: t("sidebar.navKingcup"), icon: <CrownFilled /> },
        { href: "/shitty", label: t("sidebar.navShitty"), icon: <CopyOutlined /> },
    ];

    const handleAddDrink = (event: React.MouseEvent<HTMLElement>) => {
        if (isAuthenticated && user) {
            quickAddOpener.current = event.currentTarget;
            setQuickAddOpen(true);
        }
        else if (isSsoSession) message.error(t("auth.ssoIdentityUnavailable"));
        else startSignIn();
    };
    const handleLogout = async () => {
        let queued: Awaited<ReturnType<typeof outbox.list>>;
        try {
            queued = await outbox.list();
        } catch {
            message.error(t("auth.logoutError"));
            return;
        }
        const proceed = async (discardPending: boolean) => {
            try {
                await logout(discardPending);
                message.success(t("auth.logoutSuccess"));
                navigate("/");
            } catch (error) {
                message.error(t(error instanceof PushLogoutOfflineError ? "auth.logoutPushOffline" : "auth.logoutError"));
            }
        };
        if (queued.length > 0) {
            Modal.confirm({
                title: t("auth.logoutPendingTitle"),
                content: t("auth.logoutPendingDescription", { count: queued.length }),
                okText: t("auth.logoutPendingDiscard"),
                cancelText: t("common.cancel"),
                okButtonProps: { style: { minHeight: 44 } },
                cancelButtonProps: { style: { minHeight: 44 } },
                onOk: () => proceed(true),
            });
        } else {
            await proceed(false);
        }
    };
    const handleNavigation = (event: React.MouseEvent<HTMLDivElement>) => {
        const link = (event.target as Element).closest("a");
        if (!link || link.hash === "#ui-main" || link.origin !== window.location.origin) return;
        event.preventDefault();
        navigate(link.pathname + link.search + link.hash);
    };

    return (
        <div className="sf-shell" onClickCapture={handleNavigation}>
            <AppShell
                items={items}
                currentPath={location.pathname.startsWith("/events/") && location.pathname.endsWith("/tournaments") ? "/tournaments" : location.pathname}
                brand={<div className="sf-shell-brand"><img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="" /><span>{t("sidebar.brandName")}</span></div>}
                labels={{ collapseSidebar: t("sidebar.collapse"), expandSidebar: t("sidebar.expand"), more: t("sidebar.more") }}
                moreLabel={t("sidebar.openMoreNav")}
                account={{
                    name: user?.username ?? t("sidebar.brandName"),
                    actions: [
                        { key: "theme", label: `${t("app.theme.label")}: ${mode === "dark" ? t("app.theme.light") : t("app.theme.dark")}`, icon: mode === "dark" ? <SunFilled /> : <MoonFilled />, onSelect: toggle },
                        { key: "language", label: `${t("sidebar.languageLabel")}: ${german ? t("sidebar.languageGerman") : t("sidebar.languageEnglish")}`, icon: <TranslationOutlined />, onSelect: () => { void i18n.changeLanguage(german ? "en" : "de"); } },
                        { key: "hub", label: t("sidebar.dowtexHub"), icon: <HomeOutlined />, href: dowtexHubUrl() },
                        ...((isAuthenticated && user) || isSsoSession ? [{ key: "logout", label: t("auth.logout"), icon: <LogoutOutlined />, onSelect: () => { void handleLogout(); } }] : []),
                    ],
                }}
                legal={<LegalFooter language={german ? "de" : "en"} links={{ impressum: "/impressum", privacy: "/datenschutz" }} />}
                primaryAction={(
                    <Button type="primary" aria-label={`${t("sidebar.navAddDrink")}${pendingCount > 0 ? " " + pendingCount : ""}`} onClick={handleAddDrink}>
                        {/* icon as child: the shared .ui-bottom-primary rule hides every non-.anticon span, including antd's .ant-btn-icon wrapper */}
                        <PlusOutlined />
                        <span className="ui-shell-link-label">{t("sidebar.navAddDrink")}</span>
                        {pendingCount > 0 && <Badge count={pendingCount} className="sf-shell-badge" />}
                    </Button>
                )}
            >
                <InstallPrompt />
                {isSsoSession && !user && <Alert type="warning" showIcon message={t("auth.ssoIdentityUnavailable")} />}
                {children}
            </AppShell>
            <Drawer
                className="sf-add-drink-drawer"
                aria-label={t("addDrink.title")}
                title={null}
                closable={false}
                placement={isDesktop ? "right" : "bottom"}
                open={quickAddOpen}
                onClose={() => setQuickAddOpen(false)}
                afterOpenChange={(open) => { if (!open) quickAddOpener.current?.focus(); }}
                width={520}
                height="92dvh"
                destroyOnHidden
                styles={{ body: { padding: 0, height: "100%", overflowY: "auto" } }}
            >
                <div className="sf-shell-add-content"><AddDrink embedded onRequestClose={() => setQuickAddOpen(false)} /></div>
            </Drawer>
        </div>
    );
};

export default Sidebar;
